import { createQueryKeys } from "@lukemorales/query-key-factory";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminTag } from "../types";

export type AdminTagList = Paginate<AdminTag>;

export const objectiveTags = createQueryKeys(
  "admin-performance-objective-tags",
  {
    all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
    one: (id?: number) => [id],
  },
);

export const fetchTagListQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...objectiveTags.all(page, search),
    queryFn: () =>
      adminApi
        .get<AdminTagList>(`/performance/objectives/tags`, {
          params: { page, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchTagList = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchTagListQueryOptions(page, search));

export interface AdminTagRequest {
  name: string;
  color: string;
}

export const fetchTagQueryOptions = (id?: number) =>
  queryOptions({
    ...objectiveTags.one(id),
    queryFn: () =>
      adminApi
        .get<AdminTag>(`/performance/objectives/tags/${id}`)
        .then((response) => response.data),
    enabled: id != null,
  });

export const useFetchTag = (id?: number) => useQuery(fetchTagQueryOptions(id));

export const useCreateTag = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: AdminTagRequest) =>
      adminApi.post("/performance/objectives/tags", data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: objectiveTags.all._def,
      });
    },
  });
};

export const useUpdateTag = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: AdminTagRequest) =>
      adminApi.put(`/performance/objectives/tags/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: objectiveTags.all._def,
      });
      queryClient.invalidateQueries(objectiveTags.one(id));
    },
  });
};

export const useCreateOrUpdateTag = (id: number | undefined) => {
  const create = useCreateTag();
  const update = useUpdateTag(id!);
  return id ? update : create;
};

export const useDeleteTag = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (id: number) =>
      adminApi.delete(`/performance/objectives/tags/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: objectiveTags.all._def,
      });
    },
  });
};
